import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import activityAPI from '@/services/activity'

// Async thunk to fetch activities
export const fetchActivities = createAsyncThunk(
    'activity/fetchActivities',
    async (_, { rejectWithValue }) => {
        try {
            const data = await activityAPI.fetchActivities()
            return data.activities || []
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch activities')
        }
    }
)

// Async thunk to create activity from GPX
export const createActivityFromGPX = createAsyncThunk(
    'activity/createActivityFromGPX',
    async (formData, { rejectWithValue, dispatch }) => {
        try {
            const data = await activityAPI.createActivityFromGPX(formData)
            // Refresh activities list after creation
            dispatch(fetchActivities())
            return data
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create activity')
        }
    }
)

// Async thunk to create activity from existing route
export const createActivityFromRoute = createAsyncThunk(
    'activity/createActivityFromRoute',
    async (payload, { rejectWithValue, dispatch }) => {
        try {
            const data = await activityAPI.createActivityFromRoute(payload)
            // Refresh activities list after creation
            dispatch(fetchActivities())
            return data
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create activity')
        }
    }
)

// Async thunk to update activity
export const updateActivity = createAsyncThunk(
    'activity/updateActivity',
    async ({ id, payload }, { rejectWithValue, dispatch }) => {
        try {
            const data = await activityAPI.updateActivity(id, payload)
            // Refresh activities list after update
            dispatch(fetchActivities())
            return data
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update activity')
        }
    }
)

// Async thunk to delete activity
export const deleteActivity = createAsyncThunk(
    'activity/deleteActivity',
    async (id, { rejectWithValue }) => {
        try {
            await activityAPI.deleteActivity(id)
            return id
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete activity')
        }
    }
)

const activitySlice = createSlice({
    name: 'activity',
    initialState: {
        activities: [],
        loading: false,
        submitLoading: false,
        error: null,
    },
    reducers: {
        clearError: (state) => {
            state.error = null
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch activities
            .addCase(fetchActivities.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchActivities.fulfilled, (state, action) => {
                state.loading = false
                state.activities = action.payload
            })
            .addCase(fetchActivities.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Create activity from GPX
            .addCase(createActivityFromGPX.pending, (state) => {
                state.submitLoading = true
                state.error = null
            })
            .addCase(createActivityFromGPX.fulfilled, (state) => {
                state.submitLoading = false
            })
            .addCase(createActivityFromGPX.rejected, (state, action) => {
                state.submitLoading = false
                state.error = action.payload
            })
            // Create activity from existing route
            .addCase(createActivityFromRoute.pending, (state) => {
                state.submitLoading = true
                state.error = null
            })
            .addCase(createActivityFromRoute.fulfilled, (state) => {
                state.submitLoading = false
            })
            .addCase(createActivityFromRoute.rejected, (state, action) => {
                state.submitLoading = false
                state.error = action.payload
            })
            // Update activity
            .addCase(updateActivity.pending, (state) => {
                state.submitLoading = true
                state.error = null
            })
            .addCase(updateActivity.fulfilled, (state) => {
                state.submitLoading = false
            })
            .addCase(updateActivity.rejected, (state, action) => {
                state.submitLoading = false
                state.error = action.payload
            })
            // Delete activity
            .addCase(deleteActivity.pending, (state) => {
                state.submitLoading = true
                state.error = null
            })
            .addCase(deleteActivity.fulfilled, (state, action) => {
                state.submitLoading = false
                // Optimistically remove from list
                state.activities = state.activities.filter((a) => a._id !== action.payload)
            })
            .addCase(deleteActivity.rejected, (state, action) => {
                state.submitLoading = false
                state.error = action.payload
            })
    },
})

export const { clearError } = activitySlice.actions
export default activitySlice.reducer
